import { useMemo } from 'react';
import { computeDocument, type ComputedDocument, type GstContext, type TaxRate } from '@fint/money';
import type { DiscountInput } from '@fint/validation';

export interface EditorLine {
  id: string;
  itemId?: string | null;
  name: string;
  description?: string;
  quantity: number;
  rate: string;
  unit?: string;
  hsnSac?: string;
  taxId?: string | null;
  discount?: DiscountInput;
  /**
   * Editor-only. Which "billed before" suggestion filled this line, so the
   * picker keeps reading as that pick instead of snapping back to "Free text".
   * Never sent, and never priced - a previously-billed line is free text as far
   * as the document is concerned.
   */
  pickedName?: string;
}

/**
 * Live totals as the user types.
 *
 * ── WHY THIS IS THE SAME FUNCTION THE SERVER RUNS ──
 * computeDocument comes from @fint/money, which the API imports too. One
 * implementation means the preview and the saved document cannot disagree - and
 * they would, immediately, if this were a re-derivation "close enough" for a
 * preview. A total that changes when you press Save is a total nobody trusts.
 *
 * The server's answer is still the one that gets stored. This is a preview,
 * never an input: nothing computed here is sent back.
 */
export function useInvoiceTotals(input: {
  lines: EditorLine[];
  currency: string;
  taxMode: 'exclusive' | 'inclusive';
  taxes: Array<{ id: string; name: string; percentage: string }>;
  discount?: DiscountInput;
  shippingCharge?: string;
  shippingTaxId?: string | null;
  adjustment?: string;
  gst?: GstContext;
}): ComputedDocument {
  const { lines, currency, taxMode, taxes, discount, shippingCharge, shippingTaxId, adjustment, gst } = input;

  const taxById = useMemo(() => {
    const map = new Map<string, TaxRate>();
    for (const tax of taxes) map.set(tax.id, { id: tax.id, name: tax.name, percentage: Number(tax.percentage) });
    return map;
  }, [taxes]);

  return useMemo(() => {
    // A line the user is mid-way through typing has an empty rate or a blank
    // quantity. Pricing that throws, so incomplete lines are skipped rather
    // than crashing the editor on every keystroke.
    const priceable = lines.filter((l) => l.rate !== '' && Number.isFinite(l.quantity) && l.quantity !== 0);

    try {
      return computeDocument({
        currency,
        taxMode,
        gst,
        lines: priceable.map((line) => ({
          id: line.id,
          quantity: line.quantity,
          rate: line.rate,
          discount: line.discount,
          tax: line.taxId ? taxById.get(line.taxId) : undefined,
        })),
        discount,
        shippingCharge,
        shippingTax: shippingTaxId ? taxById.get(shippingTaxId) : undefined,
        adjustment,
      });
    } catch {
      // A half-typed amount ("12.") is not a valid number yet. An empty
      // document is the honest preview until it is.
      return computeDocument({ currency, taxMode, lines: [] });
    }
  }, [lines, currency, taxMode, taxById, discount, shippingCharge, shippingTaxId, adjustment, gst]);
}
